import { useEval } from '@/context/EvalContext';
import { useEffect, useState } from 'react';

const Y_MIN = 75;
const Y_MAX = 100;
const CHART_W = 480;
const CHART_H = 160;
const PAD_L = 40;
const PAD_R = 38;
const PAD_T = 18;
const PAD_B = 42;

const innerW = CHART_W - PAD_L - PAD_R;
const innerH = CHART_H - PAD_T - PAD_B;

function toChartX(i: number, labelsLen: number) {
  return PAD_L + (i / (labelsLen - 1)) * innerW;
}
function toChartY(val: number) {
  return PAD_T + ((Y_MAX - val) / (Y_MAX - Y_MIN)) * innerH;
}
function buildLinePath(values: number[], progress: number, labelsLen: number) {
  return values.map((v, i) => {
    const animV = Y_MIN + (v - Y_MIN) * progress;
    return `${i === 0 ? 'M' : 'L'} ${toChartX(i, labelsLen)} ${toChartY(animV)}`;
  }).join(' ');
}

function buildAreaPath(values: number[], progress: number, labelsLen: number) {
  const bottomY = PAD_T + innerH;
  const points = values.map((v, i) => {
    const animV = Y_MIN + (v - Y_MIN) * progress;
    return `${toChartX(i, labelsLen)} ${toChartY(animV)}`;
  });
  const firstX = toChartX(0, labelsLen);
  const lastX = toChartX(values.length - 1, labelsLen);
  return `M ${firstX} ${bottomY} L ${points.join(' L ')} L ${lastX} ${bottomY} Z`;
}

const Y_TICKS = [75, 80, 85, 90, 95, 100];

export default function DimensionLineChart() {
  const evalData = useEval();
  const [progress, setProgress] = useState(0);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  useEffect(() => {
    const start = Date.now();
    const tick = () => {
      const t = Math.min((Date.now() - start) / 1200, 1);
      setProgress(1 - Math.pow(1 - t, 3));
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, []);

  if (!evalData) return null;
  const { labels, current, average } = evalData.courseEvalData.dimensionComparison;
  const labelsLen = labels.length;

  return (
    <div className="h-full flex flex-col" style={{ padding: '4px 8px 2px' }}>
      {/* 标题 + 图例 */}
      <div className="flex items-center justify-between px-1 mb-1.5 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 rounded-full flex-shrink-0"
            style={{ background: 'linear-gradient(to bottom, #22d3ee, rgba(34,211,238,0.5))', boxShadow: '0 0 10px rgba(34,211,238,0.9)' }} />
          <span className="text-white font-bold font-semibold tracking-wide" style={{ fontSize: '22px' }}>维度比较分析</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div style={{ width: '22px', height: '2.5px', background: '#22d3ee', borderRadius: '2px', boxShadow: '0 0 5px rgba(34,211,238,0.7)' }} />
            <span style={{ fontSize: '16px', color: 'white' }}>当前教师</span>
          </div>
          <div className="flex items-center gap-1.5">
            <svg width="22" height="3">
              <line x1="0" y1="1.5" x2="22" y2="1.5" stroke="#fbbf24" strokeWidth="2" strokeDasharray="5 3" />
            </svg>
            <span style={{ fontSize: '16px', color: 'white' }}>课程平均分</span>
          </div>
        </div>
      </div>

      {/* SVG 折线图 */}
      <div className="flex-1 min-h-0 flex items-center">
        <svg
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          style={{ width: '100%', height: '100%' }}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <filter id="lineGlow2">
              <feGaussianBlur stdDeviation="3.5" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <filter id="avgGlow">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(34,211,238,0.30)" />
              <stop offset="100%" stopColor="rgba(34,211,238,0.01)" />
            </linearGradient>
            <linearGradient id="avgAreaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(251,191,36,0.18)" />
              <stop offset="100%" stopColor="rgba(251,191,36,0.01)" />
            </linearGradient>
          </defs>

          {Y_TICKS.map(tick => {
            const y = toChartY(tick);
            return (
              <g key={tick}>
                <line x1={PAD_L} y1={y} x2={CHART_W - PAD_R} y2={y}
                  stroke="rgba(255,255,255,0.07)" strokeWidth="0.8" />
                <text x={PAD_L - 5} y={y + 4} textAnchor="end"
                  fill="#ffffff" style={{ fontSize: '12px' }}>
                  {tick}
                </text>
              </g>
            );
          })}

          {labels.map((label: string, i: number) => {
            const x = toChartX(i, labelsLen);
            const isHov = hoveredIdx === i;
            return (
              <g key={i}>
                <line x1={x} y1={PAD_T} x2={x} y2={PAD_T + innerH}
                  stroke={isHov ? 'rgba(34,211,238,0.28)' : 'rgba(255,255,255,0.04)'}
                  strokeWidth="0.8" style={{ transition: 'stroke 0.2s' }} />
                <text x={x} y={CHART_H - 4} textAnchor="middle"
                  fill="#ffffff"
                  style={{ fontSize: '12px', transition: 'fill 0.2s' }}>
                  {label}
                </text>
              </g>
            );
          })}

          {/* 面积填充 */}
          <path d={buildAreaPath(average, progress, labelsLen)} fill="url(#avgAreaGrad)" />
          <path d={buildAreaPath(current, progress, labelsLen)} fill="url(#areaGrad)" />

          {/* 折线 */}
          <path d={buildLinePath(average, progress, labelsLen)} fill="none"
            stroke="#fbbf24" strokeWidth="2.5" strokeDasharray="7 4"
            strokeLinecap="round" strokeLinejoin="round"
            filter="url(#avgGlow)" opacity="0.9" />

          <path d={buildLinePath(current, progress, labelsLen)} fill="none"
            stroke="#22d3ee" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"
            filter="url(#lineGlow2)" />

          {current.map((v: number, i: number) => {
            const animV = Y_MIN + (v - Y_MIN) * progress;
            const x = toChartX(i, labelsLen);
            const y = toChartY(animV);
            const isHov = hoveredIdx === i;
            return (
              <g key={i}>
                {isHov && (
                  <>
                    <rect x={x - 38} y={y - 52} width="76" height="36" rx="6"
                      fill="rgba(4,14,30,0.97)" stroke="rgba(34,211,238,0.5)" strokeWidth="1" />
                    <text x={x} y={y - 36} textAnchor="middle"
                      fill="#22d3ee" style={{ fontSize: '16px', fontWeight: 700 }}>
                      当前: {v}
                    </text>
                    <text x={x} y={y - 22} textAnchor="middle"
                      fill="rgba(251,191,36,0.95)" style={{ fontSize: '16px' }}>
                      均值: {average[i]}
                    </text>
                  </>
                )}
                {isHov && (
                  <circle cx={x} cy={y} r="12" fill="rgba(34,211,238,0.12)"
                    stroke="rgba(34,211,238,0.3)" strokeWidth="1" />
                )}
                <circle cx={x} cy={y} r={isHov ? 7 : 5}
                  fill={isHov ? '#fff' : '#22d3ee'}
                  stroke={isHov ? '#22d3ee' : 'rgba(0,20,30,0.7)'}
                  strokeWidth="2"
                  style={{ transition: 'r 0.15s, fill 0.15s', filter: 'drop-shadow(0 0 7px rgba(34,211,238,0.95))', cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredIdx(i)}
                  onMouseLeave={() => setHoveredIdx(null)}
                />
              </g>
            );
          })}

          {average.map((v: number, i: number) => {
            const animV = Y_MIN + (v - Y_MIN) * progress;
            return (
              <circle key={i} cx={toChartX(i, labelsLen)} cy={toChartY(animV)} r="4.5"
                fill="#fbbf24" stroke="rgba(0,0,0,0.5)" strokeWidth="1.5"
                style={{ opacity: 0.95, filter: 'drop-shadow(0 0 5px rgba(251,191,36,0.8))' }} />
            );
          })}
        </svg>
      </div>
    </div>
  );
}
